import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2, CheckCircle2, Building2, CreditCard, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/tax";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { downloadFilingPdf } from "@/pages/MerchantDashboard";

export default function FilingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paySettings, setPaySettings] = useState({ payments_enabled: true, mail_instructions: "" });

  useEffect(() => {
    api
      .get(`/filings/${id}`)
      .then((res) => setF(res.data))
      .catch(() => {
        toast.error("Filing not found");
        navigate("/dashboard");
      })
      .finally(() => setLoading(false));
    api
      .get("/settings/payments")
      .then((res) => setPaySettings(res.data))
      .catch(() => {});
  }, [id, navigate]);

  const payByCard = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        filing_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center py-24 text-slate-500 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      </div>
    );

  if (!f) return null;
  const t = f.totals || {};

  const Line = ({ n, label, gross, val, bold }) => (
    <div className={`flex items-center justify-between px-4 py-2.5 ${bold ? "bg-slate-50 font-medium" : ""}`}>
      <span className="text-sm text-slate-700">
        <span className="font-mono-data text-slate-400 mr-2">{n}</span>
        {label}
      </span>
      <span className="flex items-center gap-8">
        {gross != null && <span className="font-mono-data text-sm text-slate-400">{money(gross)}</span>}
        <span className="font-mono-data text-sm w-24 text-right text-slate-900">{money(val)}</span>
      </span>
    </div>
  );

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors mb-6"
          data-testid="detail-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="p-6 sm:p-8 border-b border-slate-200 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-emerald-700 mb-2">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm font-medium">Filing submitted</span>
              </div>
              <h1 className="font-heading text-2xl sm:text-3xl tracking-tight font-semibold text-slate-900">
                {f.business_name}
              </h1>
              <p className="font-mono-data text-sm text-slate-500 mt-1">{f.confirmation_number}</p>
              <p className="text-xs text-slate-400 mt-1">
                Submitted {new Date(f.created_at).toLocaleString()}
              </p>
            </div>
            <div className="flex flex-col items-end gap-3">
              <div className="flex items-center gap-2">
                {t.l14 > 0 && (
                  <Badge
                    data-testid="detail-payment-status"
                    className={
                      f.payment_status === "paid"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-50"
                    }
                  >
                    {f.payment_status === "paid" ? "Paid" : "Balance Due"}
                  </Badge>
                )}
                <Badge className="bg-blue-50 text-[#1E3A8A] border-blue-200 hover:bg-blue-50">
                  {f.status}
                </Badge>
              </div>
              <div className="flex gap-2">
                {t.l14 > 0 && f.payment_status !== "paid" && paySettings.payments_enabled && (
                  <Button
                    disabled={paying}
                    onClick={payByCard}
                    data-testid="pay-by-card-btn"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    {paying ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <CreditCard className="w-4 h-4 mr-2" />
                    )}
                    Pay {money(t.l14)}
                  </Button>
                )}
                <Button
                  disabled={busy === f.id}
                  onClick={() => downloadFilingPdf(f.id, f.confirmation_number, setBusy)}
                  data-testid="detail-download-pdf"
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
                >
                  {busy === f.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Download PDF
                </Button>
              </div>
            </div>
          </div>

          <div className="p-6 sm:p-8 space-y-8">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3 text-sm">
              <div className="flex items-center gap-2 text-slate-500 sm:col-span-2 mb-1">
                <Building2 className="w-4 h-4" />
                <span className="uppercase tracking-wide text-xs font-medium">Business Info</span>
              </div>
              {[
                ["Trade Name", f.trade_name || "—"],
                ["License #", f.license_number || "—"],
                ["Address", f.address],
                ["Telephone", f.telephone || "—"],
                ["Nature changed", f.nature_changed ? "Yes" : "No"],
                ["Signed by", `${f.signed_name}${f.official_title ? " · " + f.official_title : ""}`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-slate-100 py-1.5">
                  <span className="text-slate-500">{k}</span>
                  <span className="text-slate-800 text-right">{v}</span>
                </div>
              ))}
            </div>

            <div>
              <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">
                Section 1 — Final Return 2025
              </h3>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                <Line n="1a" label="Wholesale × .0010" gross={f.wholesale_2025} val={t.l1a} />
                <Line n="1b" label="Retail × .0015" gross={f.retail_2025} val={t.l1b} />
                <Line n="1c" label="Business Privilege × .0040" gross={f.biz_2025} val={t.l1c} />
                <Line n="2" label="Total" val={t.l2} bold />
                <Line n="3" label="Less estimated tax paid 2025" val={t.l3} />
                <Line n="4" label="Tax due / credit" val={t.l4} bold />
                <Line n="5" label="Penalty (10%)" val={t.l5} />
                <Line n="6" label="Interest (1.25%/mo)" val={t.l6} />
                <Line n="7" label="Total Due for 2025" val={t.l7} bold />
              </div>
            </div>

            <div>
              <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">
                Section 2 — Estimated Return 2026
              </h3>
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                <Line n="8a" label="Wholesale × .0010" gross={f.wholesale_2026} val={t.l8a} />
                <Line n="8b" label="Retail × .0015" gross={f.retail_2026} val={t.l8b} />
                <Line n="8c" label="Business Privilege × .0040" gross={f.biz_2026} val={t.l8c} />
                <Line n="9" label="Total estimated tax" val={t.l9} bold />
                <Line n="10" label="Penalty (10%)" val={t.l10} />
                <Line n="11" label="Interest (1.25%/mo)" val={t.l11} />
                <Line n="12" label="License fee ($15/category)" val={t.l12} />
                <Line n="13" label="Total Due for 2026" val={t.l13} bold />
              </div>
            </div>

            <div className="bg-[#1E3A8A] text-white rounded-md p-5 flex items-center justify-between">
              <span className="font-heading text-lg">Line 14 · Total Tax or Refund Due</span>
              <span className="font-mono-data text-2xl font-semibold" data-testid="detail-total">
                {money(t.l14)}
              </span>
            </div>

            {t.l14 > 0 && (
              f.payment_status === "paid" ? (
                <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-md p-4 text-emerald-800" data-testid="paid-banner">
                  <CheckCircle2 className="w-5 h-5 shrink-0" />
                  <p className="text-sm">
                    This balance has been paid by card{f.paid_at ? ` on ${new Date(f.paid_at).toLocaleDateString()}` : ""}.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {paySettings.payments_enabled && (
                    <div className="flex flex-wrap items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-md p-5">
                      <div>
                        <p className="font-medium text-slate-900">Pay by credit or debit card</p>
                        <p className="text-sm text-slate-500 mt-0.5">Fast, secure online payment.</p>
                      </div>
                      <Button
                        disabled={paying}
                        onClick={payByCard}
                        data-testid="pay-by-card-cta"
                        className="bg-emerald-700 hover:bg-emerald-800 text-white"
                      >
                        {paying ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Pay {money(t.l14)} Now
                      </Button>
                    </div>
                  )}
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-5" data-testid="mail-instructions">
                    <div className="flex items-center gap-2 text-amber-900 font-medium mb-2">
                      <Mail className="w-4 h-4" /> Pay by mail
                    </div>
                    <p className="text-sm text-amber-900/90 whitespace-pre-line">
                      {paySettings.mail_instructions ||
                        'Make your check or money order payable to "Cheltenham Township Finance Officer" and mail it with a printed copy of this filing to: Cheltenham Township Finance & Tax Office, 8230 Old York Road, Elkins Park, PA 19027-1589. Write your confirmation number on the check.'}
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
